import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { show_alerta } from '../../funciones';
import useCursoData from '../../hooks/useCursoData';
import CONFIG from '../../config';
import PerfilLogo from '../usuarios/PerfilLogo';

const URL_EVALUACIONES = `${CONFIG.API_URL}/operarEvaluaciones.php`;

function Evaluaciones({ idMC, rol, idCG, configuracion }) {
    const rolNum = parseInt(rol, 10);
    const esDocenteOAdmin = rolNum === 5 || rolNum === 6 || rolNum <= 4;
    const { estudiantesCurso } = useCursoData(idCG);

    const llamoTutor = localStorage.getItem('kte') || null;
    const userId = (() => {
        if (llamoTutor && llamoTutor !== 'null') {
            try {
                const partes = llamoTutor.split('&');
                if (partes.length >= 2) {
                    const est = JSON.parse(partes[1]);
                    if (est && est.estudiante_id) return est.estudiante_id;
                }
            } catch (e) {}
        }
        return localStorage.getItem('loggedUserId');
    })();

    const [evaluaciones, setEvaluaciones] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [modo, setModo] = useState('list'); // 'list' | 'crear' | 'calificar'

    // Estado del formulario crear/editar
    const [formEval, setFormEval] = useState({
        id: 0,
        titulo: '',
        descripcion: '',
        modalidad: 'escrita',
        fecha: ''
    });

    // Estado para calificar estudiantes
    const [evalSeleccionada, setEvalSeleccionada] = useState(null);
    const [notasEstudiantes, setNotasEstudiantes] = useState({});
    const [guardandoNotas, setGuardandoNotas] = useState(false);

    useEffect(() => {
        if (idCG) {
            cargarEvaluaciones();
        }
    }, [idCG]);

    const cargarEvaluaciones = async () => {
        setCargando(true);
        try {
            const res = await axios.get(`${URL_EVALUACIONES}?id_curso_grupo=${idCG}&id_estudiante=${userId}`);
            if (res.data && !res.data.error && Array.isArray(res.data.evaluaciones)) {
                setEvaluaciones(res.data.evaluaciones);
            } else {
                setEvaluaciones([]);
            }
        } catch (err) {
            console.error('Error al cargar evaluaciones:', err);
            setEvaluaciones([]);
        } finally {
            setCargando(false);
        }
    };

    const formatearFecha = (fechaStr) => {
        if (!fechaStr) return '';
        const soloFecha = String(fechaStr).split(' ')[0];
        const partes = soloFecha.split('-');
        if (partes.length === 3) {
            return `${partes[2]}/${partes[1]}/${partes[0]}`;
        }
        return soloFecha;
    };

    const abrirCrear = () => {
        setFormEval({
            id: 0,
            titulo: '',
            descripcion: '',
            modalidad: 'escrita',
            fecha: new Date().toISOString().split('T')[0]
        });
        setModo('crear');
    };

    const abrirEditar = (ev) => {
        setFormEval({
            id: ev.id,
            titulo: ev.titulo || '',
            descripcion: ev.descripcion || '',
            modalidad: ev.modalidad || 'escrita',
            fecha: (ev.fecha || '').split(' ')[0]
        });
        setModo('crear');
    };

    const guardarEvaluacion = async (e) => {
        e.preventDefault();
        if (!formEval.titulo.trim() || !formEval.fecha) {
            show_alerta('Complete el título/tema y la fecha de la evaluación', 'warning');
            return;
        }

        try {
            const payload = {
                accion: formEval.id > 0 ? 'editar' : 'crear',
                id: formEval.id,
                id_curso_grupo: idCG,
                titulo: formEval.titulo.trim(),
                descripcion: formEval.descripcion.trim(),
                modalidad: formEval.modalidad,
                fecha: formEval.fecha,
                creado_por: localStorage.getItem('loggedUserId')
            };

            const res = await axios.post(URL_EVALUACIONES, payload);
            if (res.data && !res.data.error) {
                show_alerta(res.data.mensaje || 'Evaluación guardada correctamente', 'success');
                setModo('list');
                cargarEvaluaciones();
            } else {
                show_alerta(res.data?.mensaje || 'Error al guardar la evaluación', 'error');
            }
        } catch (err) {
            console.error('Error al guardar evaluación:', err);
            show_alerta('Error al conectar con el servidor', 'error');
        }
    };

    const eliminarEvaluacion = (ev) => {
        Swal.fire({
            title: '¿Eliminar evaluación?',
            text: `Se eliminará "${ev.titulo}" y todas las calificaciones asociadas.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const res = await axios.post(URL_EVALUACIONES, {
                        accion: 'eliminar',
                        id: ev.id
                    });
                    if (res.data && !res.data.error) {
                        show_alerta('Evaluación eliminada', 'success');
                        cargarEvaluaciones();
                    } else {
                        show_alerta(res.data?.mensaje || 'No se pudo eliminar', 'error');
                    }
                } catch (err) {
                    console.error(err);
                    show_alerta('Error al eliminar la evaluación', 'error');
                }
            }
        });
    };

    const abrirCalificar = (ev) => {
        setEvalSeleccionada(ev);
        const mapaInicial = {};
        (ev.resultados || []).forEach((r) => {
            mapaInicial[r.id_estudiante] = {
                calificacion: r.calificacion || '',
                observacion: r.observacion || ''
            };
        });
        setNotasEstudiantes(mapaInicial);
        setModo('calificar');
    };

    const handleNotaChange = (idEstudiante, campo, valor) => {
        setNotasEstudiantes((prev) => ({
            ...prev,
            [idEstudiante]: {
                calificacion: prev[idEstudiante]?.calificacion || '',
                observacion: prev[idEstudiante]?.observacion || '',
                [campo]: valor
            }
        }));
    };

    const guardarResultados = async (e) => {
        if (e) e.preventDefault();
        if (!evalSeleccionada) return;

        setGuardandoNotas(true);
        try {
            const listaResultados = (estudiantesCurso || []).map((est) => ({
                id_estudiante: est.id,
                calificacion: notasEstudiantes[est.id]?.calificacion || '',
                observacion: notasEstudiantes[est.id]?.observacion || ''
            }));

            const res = await axios.post(URL_EVALUACIONES, {
                accion: 'guardar_resultados',
                id_evaluacion: evalSeleccionada.id,
                resultados: listaResultados
            });

            if (res.data && !res.data.error) {
                show_alerta('Calificaciones guardadas correctamente', 'success');
                await cargarEvaluaciones();
                setModo('list');
            } else {
                show_alerta(res.data?.mensaje || 'Error al guardar calificaciones', 'error');
            }
        } catch (err) {
            console.error('Error al guardar resultados:', err);
            show_alerta('Error al conectar con el servidor', 'error');
        } finally {
            setGuardandoNotas(false);
        }
    };

    return (
        <div className="mt-3">
            {modo === 'list' && (
                <div className="card shadow-sm border-0 p-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                        <div>
                            <h4 className="mb-1">
                                <i className="fa-solid fa-file-signature text-primary me-2"></i>
                                Evaluaciones del Espacio
                            </h4>
                            <p className="text-muted small mb-0">
                                {esDocenteOAdmin
                                    ? 'Gestione las evaluaciones orales y escritas del curso y registre las calificaciones de cada estudiante.'
                                    : 'Consulte el detalle de las evaluaciones programadas y sus calificaciones.'}
                            </p>
                        </div>
                        {esDocenteOAdmin && (
                            <button
                                type="button"
                                className="btn btn-dark btn-sm px-3 py-2"
                                onClick={abrirCrear}
                            >
                                <i className="fa-solid fa-plus me-1"></i> Crear evaluación
                            </button>
                        )}
                    </div>

                    {cargando ? (
                        <div className="text-center py-4 text-muted">
                            <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                            Cargando evaluaciones...
                        </div>
                    ) : evaluaciones.length === 0 ? (
                        <div className="alert alert-light border text-center py-4 mb-0">
                            <i className="fa-regular fa-folder-open fs-3 text-muted d-block mb-2"></i>
                            No hay evaluaciones registradas en este curso todavía.
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Título / Tema</th>
                                        <th>Modalidad</th>
                                        <th>Descripción</th>
                                        {esDocenteOAdmin ? (
                                            <>
                                                <th className="text-center">Calificados</th>
                                                <th className="text-end">Acciones</th>
                                            </>
                                        ) : (
                                            <th className="text-center">Mi Calificación</th>
                                        )}
                                    </tr>
                                </thead>
                                <tbody>
                                    {evaluaciones.map((ev) => {
                                        const esOral = String(ev.modalidad).toLowerCase() === 'oral';
                                        const cantCalificados = Array.isArray(ev.resultados) ? ev.resultados.length : 0;
                                        const totalEst = Array.isArray(estudiantesCurso) ? estudiantesCurso.length : 0;
                                        const miRes = ev.mi_resultado || (ev.resultados || []).find(r => String(r.id_estudiante) === String(userId));

                                        return (
                                            <tr key={ev.id}>
                                                <td className="text-nowrap fw-semibold">
                                                    <i className="fa-regular fa-calendar text-secondary me-1"></i>
                                                    {formatearFecha(ev.fecha)}
                                                </td>
                                                <td className="fw-bold">{ev.titulo}</td>
                                                <td>
                                                    <span className={`badge ${esOral ? 'bg-info text-dark' : 'bg-primary'}`}>
                                                        <i className={`fa-solid ${esOral ? 'fa-microphone-lines' : 'fa-pen-to-square'} me-1`}></i>
                                                        {esOral ? 'Oral' : 'Escrita'}
                                                    </span>
                                                </td>
                                                <td className="small text-muted" style={{ maxWidth: '320px' }}>
                                                    {ev.descripcion || <span className="fst-italic">Sin descripción</span>}
                                                </td>
                                                {esDocenteOAdmin ? (
                                                    <>
                                                        <td className="text-center">
                                                            <span className={`badge ${cantCalificados > 0 ? 'bg-success' : 'bg-secondary'}`}>
                                                                {cantCalificados} {totalEst > 0 ? `/ ${totalEst}` : ''}
                                                            </span>
                                                        </td>
                                                        <td className="text-end text-nowrap">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-primary me-1"
                                                                onClick={() => abrirCalificar(ev)}
                                                                title="Cargar o editar calificaciones de los estudiantes"
                                                            >
                                                                <i className="fa-solid fa-list-check me-1"></i>
                                                                Calificar
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary me-1"
                                                                onClick={() => abrirEditar(ev)}
                                                                title="Editar evaluación"
                                                            >
                                                                <i className="fa-solid fa-pen"></i>
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => eliminarEvaluacion(ev)}
                                                                title="Eliminar evaluación"
                                                            >
                                                                <i className="fa-solid fa-trash"></i>
                                                            </button>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <td className="text-center">
                                                        {miRes && miRes.calificacion ? (
                                                            <div>
                                                                <span className="badge bg-success fs-6 px-3 py-1">
                                                                    {miRes.calificacion}
                                                                </span>
                                                                {miRes.observacion && (
                                                                    <div className="small text-muted mt-1">
                                                                        <i className="fa-regular fa-comment-dots me-1"></i>
                                                                        {miRes.observacion}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="badge bg-light text-secondary border">
                                                                Pendiente de calificación
                                                            </span>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {modo === 'crear' && esDocenteOAdmin && (
                <div className="card shadow-sm border-0 p-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">
                            <i className="fa-solid fa-file-circle-plus text-primary me-2"></i>
                            {formEval.id > 0 ? 'Editar Evaluación' : 'Crear Nueva Evaluación'}
                        </h5>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => setModo('list')}
                        >
                            <i className="fa-solid fa-arrow-left me-1"></i> Volver
                        </button>
                    </div>

                    {formEval.id === 0 && (
                        <div className="alert alert-info py-2 px-3 small mb-3">
                            <i className="fa-regular fa-calendar-check me-2"></i>
                            Al crear la evaluación, se generará automáticamente un evento recordatorio en la <strong>Agenda</strong> para todos los estudiantes del curso.
                        </div>
                    )}

                    <form onSubmit={guardarEvaluacion}>
                        <div className="row g-3">
                            <div className="col-12 col-md-6">
                                <label className="form-label fw-bold">Título / Tema *</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Ej: Evaluación Unidad 1 - Fracciones"
                                    value={formEval.titulo}
                                    onChange={(e) => setFormEval({ ...formEval, titulo: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="col-12 col-md-3">
                                <label className="form-label fw-bold">Modalidad *</label>
                                <select
                                    className="form-select"
                                    value={formEval.modalidad}
                                    onChange={(e) => setFormEval({ ...formEval, modalidad: e.target.value })}
                                    required
                                >
                                    <option value="escrita">Escrita</option>
                                    <option value="oral">Oral</option>
                                </select>
                            </div>

                            <div className="col-12 col-md-3">
                                <label className="form-label fw-bold">Fecha *</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={formEval.fecha}
                                    onChange={(e) => setFormEval({ ...formEval, fecha: e.target.value })}
                                    required
                                />
                            </div>

                            <div className="col-12">
                                <label className="form-label fw-bold">Descripción / Temario</label>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    placeholder="Describa los contenidos, criterios o indicaciones de la evaluación..."
                                    value={formEval.descripcion}
                                    onChange={(e) => setFormEval({ ...formEval, descripcion: e.target.value })}
                                ></textarea>
                            </div>
                        </div>

                        <div className="mt-4 d-flex justify-content-end gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setModo('list')}
                            >
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-success">
                                <i className="fa-solid fa-floppy-disk me-1"></i>
                                {formEval.id > 0 ? 'Guardar Cambios' : 'Crear Evaluación'}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {modo === 'calificar' && evalSeleccionada && esDocenteOAdmin && (
                <div className="card shadow-sm border-0 p-4">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                        <div>
                            <h5 className="mb-1">
                                <i className="fa-solid fa-list-check text-primary me-2"></i>
                                Resultados de Evaluación: <strong>{evalSeleccionada.titulo}</strong>
                            </h5>
                            <div className="small text-muted">
                                <span className="me-3">
                                    <i className="fa-regular fa-calendar me-1"></i>
                                    Fecha: <strong>{formatearFecha(evalSeleccionada.fecha)}</strong>
                                </span>
                                <span className="me-3">
                                    Modalidad:{' '}
                                    <span className={`badge ${evalSeleccionada.modalidad === 'oral' ? 'bg-info text-dark' : 'bg-primary'}`}>
                                        {evalSeleccionada.modalidad === 'oral' ? 'Oral' : 'Escrita'}
                                    </span>
                                </span>
                                {evalSeleccionada.descripcion && (
                                    <span>• {evalSeleccionada.descripcion}</span>
                                )}
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => setModo('list')}
                            >
                                <i className="fa-solid fa-arrow-left me-1"></i> Volver
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={guardarResultados}
                                disabled={guardandoNotas}
                            >
                                <i className="fa-solid fa-floppy-disk me-1"></i>
                                {guardandoNotas ? 'Guardando...' : 'Guardar Calificaciones'}
                            </button>
                        </div>
                    </div>

                    <datalist id="sugerencias-calificacion-eval">
                        <option value="10" />
                        <option value="9" />
                        <option value="8" />
                        <option value="7" />
                        <option value="6" />
                        <option value="5" />
                        <option value="4" />
                        <option value="3" />
                        <option value="2" />
                        <option value="1" />
                        <option value="TEA" />
                        <option value="TEP" />
                        <option value="TED" />
                        <option value="Aprobado" />
                        <option value="Desaprobado" />
                    </datalist>

                    {!estudiantesCurso || estudiantesCurso.length === 0 ? (
                        <div className="alert alert-warning small">
                            No se encontraron estudiantes inscriptos en este curso.
                        </div>
                    ) : (
                        <form onSubmit={guardarResultados}>
                            <div className="table-responsive">
                                <table className="table table-hover align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Estudiante</th>
                                            <th style={{ width: '220px' }}>Calificación</th>
                                            <th>Observación / Devolución (opcional)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {estudiantesCurso.map((est) => {
                                            const notaActual = notasEstudiantes[est.id]?.calificacion || '';
                                            const obsActual = notasEstudiantes[est.id]?.observacion || '';

                                            return (
                                                <tr key={est.id}>
                                                    <td>
                                                        <PerfilLogo
                                                            usuario={est}
                                                            version="extendida+"
                                                            configuracion={configuracion}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            list="sugerencias-calificacion-eval"
                                                            className="form-control form-control-sm fw-bold"
                                                            placeholder="Ej: 8, TEA, Aprobado..."
                                                            value={notaActual}
                                                            onChange={(e) => handleNotaChange(est.id, 'calificacion', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm"
                                                            placeholder="Comentario u observación para el estudiante..."
                                                            value={obsActual}
                                                            onChange={(e) => handleNotaChange(est.id, 'observacion', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="text-end mt-3">
                                <button
                                    type="button"
                                    className="btn btn-secondary me-2"
                                    onClick={() => setModo('list')}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-success"
                                    disabled={guardandoNotas}
                                >
                                    <i className="fa-solid fa-floppy-disk me-1"></i>
                                    {guardandoNotas ? 'Guardando...' : 'Guardar Calificaciones'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
}

export default Evaluaciones;

